import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, FileArrowDown, ArrowClockwise, ShieldCheck, Warning } from "@phosphor-icons/react";

const CLOCK = {
  overdue: "bg-red-100 text-red-700 border-red-300",
  due_soon: "bg-amber-100 text-amber-800 border-amber-300",
  current: "bg-green-100 text-green-700 border-green-300",
  none: "bg-slate-100 text-slate-500 border-slate-300",
};
const clockLabel = (c) => {
  if (!c || c.status === "none") return "—";
  if (c.status === "overdue") return `Overdue ${Math.abs(c.days_left)}d`;
  if (c.status === "due_soon") return `Due in ${c.days_left}d`;
  return c.due ? `Due ${c.due}` : "On track";
};
const Clock = ({ c, testid }) => (
  <span data-testid={testid} className={`text-xs px-2 py-0.5 rounded border ${CLOCK[c?.status || "none"]}`}>{clockLabel(c)}</span>
);
const VA_TYPE_LABEL = { paper: "Paper", active: "Active", active_test: "Active (test env)", pre_production: "Pre-production" };
const REM_LABEL = { open: "Open", in_progress: "In progress", complete: "Complete" };

function Stat({ label, value, danger, testid }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="overline text-muted-foreground text-xs">{label}</div>
      <div data-testid={testid} className={`text-2xl font-bold mt-1 ${danger && value > 0 ? "text-red-600" : ""}`}>{value}</div>
    </div>
  );
}

export default function Cip010Compliance() {
  const { isRole } = useAuth();
  const canEdit = isRole("admin", "analyst");
  const [tab, setTab] = useState("monitoring");
  const [summary, setSummary] = useState(null);
  const [mon, setMon] = useState({ events: [], clocks: [] });
  const [va, setVa] = useState({ assessments: [], clocks: [], types: [], remediation_status: [] });
  const [settings, setSettings] = useState(null);
  const [modal, setModal] = useState(null); // {type:'monitor'|'va', data}
  const [denied, setDenied] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [su, m, v, st] = await Promise.all([
        api.get("/cip010/r2r3/summary"), api.get("/cip010/monitoring"),
        api.get("/cip010/vuln-assessments"), api.get("/cip010/r2r3/settings"),
      ]);
      setSummary(su.data); setMon(m.data); setVa(v.data); setSettings(st.data); setDenied(false);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, []);

  useEffect(() => { loadAll(); api.post("/cip010/r2r3/process/run").catch(() => {}); }, [loadAll]);

  const runProcess = async () => {
    try {
      const { data } = await api.post("/cip010/r2r3/process/run");
      if (data.reason === "process-disabled") toast.info("Recurring process is disabled — enable it in Settings.");
      else toast.success(`ClaroFlow sync: ${data.created} ticket(s), ${data.alerts} alert(s)`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doExport = async () => {
    try { await downloadBlob("/cip010/r2r3-export", "CIP010_R2_R3_Evidence.docx"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/cip010/r2r3/settings", { ...settings, ...patch });
      setSettings(data); toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delMonitor = async (id) => { try { await api.delete(`/cip010/monitoring/${id}`); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const delVa = async (id) => { try { await api.delete(`/cip010/vuln-assessments/${id}`); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const noAssets = summary && summary.monitored_assets === 0;

  return (
    <div data-testid="cip010-compliance-page">
      <PageHeader title="CIP-010 R2 & R3 — Monitoring & Vulnerability Assessments"
        subtitle="Config change monitoring (35 cal-days) and vulnerability assessments (15 / 36 cal-months)"
        action={
          <div className="flex gap-2">
            <Button data-testid="c10-sync-btn" variant="outline" className="rounded-none" onClick={runProcess}><ArrowClockwise size={15} className="mr-1" />Sync ClaroFlow</Button>
            <Button data-testid="c10-export-btn" className="rounded-none bg-primary hover:bg-primary/90" onClick={doExport}><FileArrowDown size={15} className="mr-1" />R2/R3 Evidence</Button>
          </div>
        } />
      <div className="p-8 space-y-6">
        {denied && (
          <div data-testid="c10-bcsi-denied" className="flex items-start gap-2 border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
            <Warning size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-amber-800">BCSI access required. This module handles BES Cyber System Information — ask an administrator to grant you BCSI access to view CIP-010 R2/R3.</p>
          </div>
        )}
        {summary && (
          <div data-testid="c10-summary" className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Stat label="Monitored assets" value={summary.monitored_assets} testid="c10-stat-assets" />
            <Stat label="Monitoring overdue" value={summary.monitor_overdue} danger testid="c10-stat-mon-overdue" />
            <Stat label="Monitoring due soon" value={summary.monitor_due_soon} testid="c10-stat-mon-soon" />
            <Stat label="VA overdue" value={summary.va_overdue} danger testid="c10-stat-va-overdue" />
            <Stat label="VA due soon" value={summary.va_due_soon} testid="c10-stat-va-soon" />
            <Stat label="Open remediation" value={summary.open_remediation} danger testid="c10-stat-remediation" />
          </div>
        )}
        {noAssets && (
          <div data-testid="c10-no-assets" className="flex items-start gap-2 border border-border bg-secondary/40 px-4 py-3 text-sm">
            <Warning size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-muted-foreground">No configuration-managed Cyber Assets yet. Capture a CIP-010 baseline (in <span className="font-semibold">ClaroPatch</span>) to bring assets into R2 monitoring scope.</p>
          </div>
        )}

        <div className="inline-flex border border-border">
          {[["monitoring", "Config Monitoring (R2)"], ["va", "Vulnerability Assessments (R3)"], ["settings", "Settings"]].map(([v, l]) => (
            <button key={v} data-testid={`c10-tab-${v}`} onClick={() => setTab(v)}
              className={`px-4 py-1.5 text-xs font-semibold ${tab === v ? "bg-primary text-primary-foreground" : "hover:bg-secondary"} ${v !== "monitoring" ? "border-l border-border" : ""}`}>{l}</button>
          ))}
        </div>

        {tab === "monitoring" && (
          <div className="space-y-6">
            <div className="border border-border bg-card">
              <div className="border-b border-border px-4 py-3 flex items-center justify-between">
                <div><div className="font-display font-bold text-sm">Per-asset monitoring clocks</div><div className="text-xs text-muted-foreground">R2.1 — monitor each baseline at least once every 35 calendar days.</div></div>
                {canEdit && <Button data-testid="c10-add-monitor-btn" size="sm" className="rounded-none" onClick={() => setModal({ type: "monitor", data: { asset_id: mon.clocks[0]?.asset_id || "", method: "manual", deviations_found: 0, deviations_authorized: true, investigation_notes: "", notes: "" } })}><Plus size={14} className="mr-1" />Log Monitoring</Button>}
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                  <th className="px-4 py-2">Cyber Asset</th><th className="px-4 py-2">Last Monitored</th><th className="px-4 py-2">Next Due</th><th className="px-4 py-2">Status</th>
                </tr></thead>
                <tbody>
                  {mon.clocks.map((c) => (
                    <tr key={c.asset_id} data-testid={`c10-mon-clock-${c.asset_id}`} className="border-b border-border">
                      <td className="px-4 py-2 font-medium">{c.asset_name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{c.last_monitored || "never"}</td>
                      <td className="px-4 py-2 font-mono text-xs">{c.due || "—"}</td>
                      <td className="px-4 py-2"><Clock c={c} testid={`c10-mon-badge-${c.asset_id}`} /></td>
                    </tr>
                  ))}
                  {mon.clocks.length === 0 && <tr><td colSpan={4} className="px-4 py-4 text-muted-foreground text-sm">No monitored assets.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="border border-border bg-card">
              <div className="border-b border-border px-4 py-3"><div className="font-display font-bold text-sm">Monitoring log</div></div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                  <th className="px-4 py-2">Date</th><th className="px-4 py-2">Asset</th><th className="px-4 py-2">Method</th><th className="px-4 py-2">Deviations</th><th className="px-4 py-2">Authorized?</th><th className="px-4 py-2"></th>
                </tr></thead>
                <tbody>
                  {mon.events.map((e) => (
                    <tr key={e.id} data-testid={`c10-mon-event-${e.id}`} className="border-b border-border">
                      <td className="px-4 py-2 font-mono text-xs">{e.monitor_date}</td>
                      <td className="px-4 py-2">{e.asset_name}</td>
                      <td className="px-4 py-2 capitalize">{e.method}</td>
                      <td className="px-4 py-2">{e.deviations_found}</td>
                      <td className="px-4 py-2">{e.deviations_authorized ? <span className="text-green-700">Yes</span> : <span className="text-red-600 font-semibold">No — investigated</span>}</td>
                      <td className="px-4 py-2">{canEdit && <button data-testid={`c10-mon-del-${e.id}`} onClick={() => delMonitor(e.id)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>}</td>
                    </tr>
                  ))}
                  {mon.events.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-muted-foreground text-sm">No monitoring events logged yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "va" && (
          <div className="space-y-6">
            <div className="border border-border bg-card">
              <div className="border-b border-border px-4 py-3 flex items-center justify-between">
                <div><div className="font-display font-bold text-sm">Per-asset assessment clocks</div><div className="text-xs text-muted-foreground">R3.1 — any VA every 15 cal-months. R3.2 — active VA (test env) every 36 cal-months.</div></div>
                {canEdit && <Button data-testid="c10-add-va-btn" size="sm" className="rounded-none" onClick={() => setModal({ type: "va", data: { asset_id: va.clocks[0]?.asset_id || "", scope_label: "", assessment_type: "paper", assessment_date: "", assessor: "", findings: 0, action_plan: "", remediation_status: "open", remediation_target_date: "", notes: "" } })}><Plus size={14} className="mr-1" />Record Assessment</Button>}
              </div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                  <th className="px-4 py-2">Cyber Asset</th><th className="px-4 py-2">R3.1 (15 mo)</th><th className="px-4 py-2">R3.2 (36 mo)</th>
                </tr></thead>
                <tbody>
                  {va.clocks.map((c) => (
                    <tr key={c.asset_id} data-testid={`c10-va-clock-${c.asset_id}`} className="border-b border-border">
                      <td className="px-4 py-2 font-medium">{c.asset_name}</td>
                      <td className="px-4 py-2"><Clock c={c.r3_1} testid={`c10-va-r31-${c.asset_id}`} /> <span className="text-[11px] text-muted-foreground ml-1">{c.r3_1?.last || "never"}</span></td>
                      <td className="px-4 py-2"><Clock c={c.r3_2} testid={`c10-va-r32-${c.asset_id}`} /> <span className="text-[11px] text-muted-foreground ml-1">{c.r3_2?.last || "never"}</span></td>
                    </tr>
                  ))}
                  {va.clocks.length === 0 && <tr><td colSpan={3} className="px-4 py-4 text-muted-foreground text-sm">No monitored assets.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="border border-border bg-card">
              <div className="border-b border-border px-4 py-3"><div className="font-display font-bold text-sm">Assessment records (R3.4)</div></div>
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left overline text-muted-foreground">
                  <th className="px-4 py-2">Date</th><th className="px-4 py-2">Scope</th><th className="px-4 py-2">Type</th><th className="px-4 py-2">Findings</th><th className="px-4 py-2">Remediation</th><th className="px-4 py-2"></th>
                </tr></thead>
                <tbody>
                  {va.assessments.map((v) => (
                    <tr key={v.id} data-testid={`c10-va-row-${v.id}`} className="border-b border-border">
                      <td className="px-4 py-2 font-mono text-xs">{v.assessment_date}</td>
                      <td className="px-4 py-2">{v.asset_name || v.scope_label || "—"}</td>
                      <td className="px-4 py-2">{VA_TYPE_LABEL[v.assessment_type] || v.assessment_type}</td>
                      <td className="px-4 py-2">{v.findings}</td>
                      <td className="px-4 py-2"><span className={v.remediation_status === "complete" ? "text-green-700" : (v.findings > 0 ? "text-amber-700" : "text-muted-foreground")}>{REM_LABEL[v.remediation_status] || v.remediation_status}</span>{v.remediation_target_date && <span className="text-[11px] text-muted-foreground ml-1">(target {v.remediation_target_date})</span>}</td>
                      <td className="px-4 py-2 flex gap-2">
                        {canEdit && <button data-testid={`c10-va-edit-${v.id}`} onClick={() => setModal({ type: "va", data: { ...v, assessment_date: v.assessment_date || "", remediation_target_date: v.remediation_target_date || "" } })} className="text-xs underline text-muted-foreground hover:text-foreground">edit</button>}
                        {canEdit && <button data-testid={`c10-va-del-${v.id}`} onClick={() => delVa(v.id)} className="text-muted-foreground hover:text-red-600"><Trash size={15} /></button>}
                      </td>
                    </tr>
                  ))}
                  {va.assessments.length === 0 && <tr><td colSpan={6} className="px-4 py-4 text-muted-foreground text-sm">No assessments recorded yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="border border-border bg-card p-5 max-w-xl space-y-4" data-testid="c10-settings">
            <div className="flex items-start gap-2">
              <Checkbox data-testid="c10-settings-enabled" checked={settings.process_enabled} onCheckedChange={(v) => saveSettings({ process_enabled: !!v })} className="rounded-none mt-1" disabled={!canEdit} />
              <div><Label className="font-semibold">Enable recurring process</Label><p className="text-xs text-muted-foreground">When on, ClaroFlow tickets and detective PNC alerts are raised for overdue 35-day monitoring (R2.1) and overdue vulnerability assessments (R3.1/R3.2).</p></div>
            </div>
            <div><Label>Process assignee (email)</Label>
              <Input data-testid="c10-settings-assignee" value={settings.process_assignee || ""} onChange={(e) => setSettings({ ...settings, process_assignee: e.target.value })} onBlur={(e) => canEdit && saveSettings({ process_assignee: e.target.value })} placeholder="analyst@yourentity.com" className="rounded-none mt-1" disabled={!canEdit} />
            </div>
          </div>
        )}
      </div>

      {modal && <RecordModal modal={modal} clocks={modal.type === "monitor" ? mon.clocks : va.clocks} va={va} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadAll(); }} />}
    </div>
  );
}

function RecordModal({ modal, clocks, va, onClose, onSaved }) {
  const [f, setF] = useState(modal.data);
  const isMonitor = modal.type === "monitor";
  const isEdit = !!f.id;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try {
      if (isMonitor) {
        if (!f.asset_id) return toast.error("Select a Cyber Asset");
        await api.post("/cip010/monitoring", { ...f, deviations_found: parseInt(f.deviations_found, 10) || 0 });
      } else {
        if (!f.asset_id && !f.scope_label.trim()) return toast.error("Select an asset or enter a group scope label");
        const body = { ...f, findings: parseInt(f.findings, 10) || 0 };
        if (isEdit) await api.patch(`/cip010/vuln-assessments/${f.id}`, body);
        else await api.post("/cip010/vuln-assessments", body);
      }
      toast.success(isMonitor ? "Monitoring logged" : "Assessment saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} />{isMonitor ? "Log Baseline Monitoring (R2.1)" : (isEdit ? "Edit Assessment" : "Record Vulnerability Assessment (R3)")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Cyber Asset{!isMonitor && " (or leave blank for a group assessment)"}</Label>
            <Select value={f.asset_id || "none"} onValueChange={(v) => set("asset_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="c10-modal-asset" className="rounded-none mt-1"><SelectValue placeholder="Select asset" /></SelectTrigger>
              <SelectContent>
                {!isMonitor && <SelectItem value="none">— Group / no single asset —</SelectItem>}
                {clocks.map((c) => <SelectItem key={c.asset_id} value={c.asset_id}>{c.asset_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isMonitor ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Monitoring date</Label><Input data-testid="c10-modal-date" type="date" value={f.monitor_date || ""} onChange={(e) => set("monitor_date", e.target.value)} className="rounded-none mt-1" /></div>
                <div><Label>Method</Label>
                  <Select value={f.method} onValueChange={(v) => set("method", v)}>
                    <SelectTrigger data-testid="c10-modal-method" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="manual">Manual</SelectItem><SelectItem value="automated">Automated</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Deviations detected</Label><Input data-testid="c10-modal-deviations" type="number" min="0" value={f.deviations_found} onChange={(e) => set("deviations_found", e.target.value)} className="rounded-none mt-1" /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox data-testid="c10-modal-authorized" checked={f.deviations_authorized} onCheckedChange={(v) => set("deviations_authorized", !!v)} className="rounded-none" />All detected deviations were authorized</label>
              {!f.deviations_authorized && <div><Label>Investigation notes (unauthorized changes)</Label><Textarea data-testid="c10-modal-investigation" value={f.investigation_notes} onChange={(e) => set("investigation_notes", e.target.value)} className="rounded-none mt-1" /></div>}
            </>
          ) : (
            <>
              <div><Label>Group scope label (if not a single asset)</Label><Input data-testid="c10-modal-scope" value={f.scope_label} onChange={(e) => set("scope_label", e.target.value)} placeholder="e.g. All Medium-impact BCS" className="rounded-none mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Assessment type</Label>
                  <Select value={f.assessment_type} onValueChange={(v) => set("assessment_type", v)}>
                    <SelectTrigger data-testid="c10-modal-vatype" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{(va.types || []).map((t) => <SelectItem key={t} value={t}>{VA_TYPE_LABEL[t] || t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Assessment date</Label><Input data-testid="c10-modal-vadate" type="date" value={f.assessment_date || ""} onChange={(e) => set("assessment_date", e.target.value)} className="rounded-none mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Assessor</Label><Input data-testid="c10-modal-assessor" value={f.assessor} onChange={(e) => set("assessor", e.target.value)} className="rounded-none mt-1" /></div>
                <div><Label>Findings (count)</Label><Input data-testid="c10-modal-findings" type="number" min="0" value={f.findings} onChange={(e) => set("findings", e.target.value)} className="rounded-none mt-1" /></div>
              </div>
              <div><Label>Remediation action plan (R3.4)</Label><Textarea data-testid="c10-modal-actionplan" value={f.action_plan} onChange={(e) => set("action_plan", e.target.value)} className="rounded-none mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Remediation status</Label>
                  <Select value={f.remediation_status} onValueChange={(v) => set("remediation_status", v)}>
                    <SelectTrigger data-testid="c10-modal-remstatus" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{(va.remediation_status || []).map((s) => <SelectItem key={s} value={s}>{REM_LABEL[s] || s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Remediation target date</Label><Input data-testid="c10-modal-remtarget" type="date" value={f.remediation_target_date || ""} onChange={(e) => set("remediation_target_date", e.target.value)} className="rounded-none mt-1" /></div>
              </div>
            </>
          )}
          <Button data-testid="c10-modal-save" onClick={save} className="w-full rounded-none bg-primary hover:bg-primary/90">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
